import { useState } from "react";

import { platform, type PlatformMember, type PlatformOrg } from "../lib/api.js";
import { relativeTime } from "../lib/freshness.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * Every organization on the platform and who is in it.
 *
 * Drawn as an actual tree rather than a grouped table because the interesting
 * questions here are shaped like a tree: which organizations exist, who is in
 * more than one, and who is in none. The last of those is the reason this
 * view is worth having at all -- an account in no organization appears in no
 * organization's user list, by definition, so this is the only place it shows.
 */

const REFRESH_MS = 120_000;

export function OrgUserTree() {
  const now = useNow();
  const { data, error, reload } = useLiveData(() => platform.users(), REFRESH_MS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const setMembership = async (org: PlatformOrg, join: boolean) => {
    if (!join && !confirm(`Leave ${org.name}? You'll need to be added back to return.`)) return;
    setBusy(org.id);
    try {
      await (join ? platform.join(org.id) : platform.leave(org.id));
      reload();
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) {
    return <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${error}`}</p>;
  }
  if (!data) return <p style={{ color: "var(--text-muted)" }}>LOADING...</p>;

  const byOrg = new Map<string, PlatformMember[]>();
  for (const member of data.members) {
    const list = byOrg.get(member.tenant_id) ?? [];
    list.push(member);
    byOrg.set(member.tenant_id, list);
  }

  return (
    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
      {data.organizations.map((org) => {
        const members = byOrg.get(org.id) ?? [];
        const isCollapsed = collapsed[org.id] ?? false;

        return (
          <div key={org.id} style={{ marginBottom: 14 }}>
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [org.id]: !isCollapsed }))}
              style={orgButton}
            >
              <span style={{ color: "var(--text-muted)" }}>{isCollapsed ? "[+]" : "[-]"}</span>{" "}
              <span style={{ color: "var(--text-primary)" }}>{org.name}</span>{" "}
              <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                {org.slug} · {org.device_count} device{org.device_count === "1" ? "" : "s"} ·{" "}
                {members.length} {members.length === 1 ? "person" : "people"}
              </span>
            </button>

            {/* An organization nobody owns cannot administer itself: no one in
                it can add users or manage its own devices. */}
            {org.has_owner === false && members.length > 0 && (
              <span style={{ color: "var(--status-warning)", fontSize: 10, marginLeft: 8 }}>
                [NO OWNER]
              </span>
            )}

            <button
              disabled={busy === org.id}
              onClick={() => void setMembership(org, !org.i_am_member)}
              style={{
                ...joinButton,
                color: org.i_am_member ? "var(--status-warning)" : "var(--text-secondary)",
              }}
            >
              {org.i_am_member ? "[ LEAVE ]" : "[ JOIN ]"}
            </button>

            {!isCollapsed &&
              members.map((member, index) => (
                <div key={member.user_id} style={{ paddingLeft: 8, whiteSpace: "nowrap", overflowX: "auto" }}>
                  <span style={{ color: "var(--baseline)" }}>
                    {index === members.length - 1 ? "└─ " : "├─ "}
                  </span>
                  <span style={{ color: member.is_active ? "var(--text-primary)" : "var(--text-muted)" }}>
                    {member.email}
                  </span>{" "}
                  <span style={{ color: "var(--text-secondary)", fontSize: 11 }}>
                    {member.role.toUpperCase()}
                  </span>{" "}
                  <MemberFlags member={member} now={now} />
                </div>
              ))}

            {!isCollapsed && members.length === 0 && (
              <div style={{ paddingLeft: 8, color: "var(--text-muted)", fontSize: 12 }}>
                <span style={{ color: "var(--baseline)" }}>└─ </span>
                no members
              </div>
            )}
          </div>
        );
      })}

      {/* The state that exists nowhere else. Kept visually distinct because it
          is a problem to fix, not a category of user. */}
      {data.unassigned.length > 0 && (
        <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div style={{ color: "var(--status-warning)", letterSpacing: "0.1em", fontSize: 12 }}>
            ACCOUNTS IN NO ORGANIZATION
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "4px 0 8px", lineHeight: 1.5 }}>
            Not in any organization, so they can't sign in.
          </p>
          {data.unassigned.map((account, index) => (
            <div key={account.user_id} style={{ paddingLeft: 8 }}>
              <span style={{ color: "var(--baseline)" }}>
                {index === data.unassigned.length - 1 ? "└─ " : "├─ "}
              </span>
              <span style={{ color: "var(--status-warning)" }}>{account.email}</span>
              {account.is_platform_admin && (
                <span style={{ color: "var(--text-muted)", fontSize: 11 }}> platform admin</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The states worth seeing at a glance on a platform-wide view. */
function MemberFlags({ member, now }: { member: PlatformMember; now: number }) {
  const flags: { label: string; color: string }[] = [];

  if (!member.is_active) flags.push({ label: "DISABLED", color: "var(--status-critical)" });
  if (member.must_change_password) flags.push({ label: "TEMP PASSWORD", color: "var(--status-warning)" });
  if (member.is_platform_admin) flags.push({ label: "PLATFORM ADMIN", color: "var(--status-serious)" });
  // Only interesting above one: it is what makes this a tree rather than a
  // list, and the thing an ordinary per-organization view cannot show.
  if (Number(member.org_count) > 1) {
    flags.push({ label: `IN ${member.org_count} ORGS`, color: "var(--series-6)" });
  }
  if (member.has_google) flags.push({ label: "GOOGLE", color: "var(--text-muted)" });

  return (
    <>
      {flags.map((flag) => (
        <span key={flag.label} style={{ color: flag.color, fontSize: 10, letterSpacing: "0.06em" }}>
          {`[${flag.label}] `}
        </span>
      ))}
      <span style={{ color: "var(--text-muted)", fontSize: 10 }}>
        {member.last_seen_at ? `seen ${relativeTime(member.last_seen_at, now)}` : "never signed in"}
      </span>
    </>
  );
}

const joinButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "1px 6px",
  fontSize: 10, cursor: "pointer", letterSpacing: "0.06em", marginLeft: 8,
};

const orgButton: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  textTransform: "none",
  letterSpacing: "inherit",
};
